import { Button, Flex, Icon, Popover, Portal, Text } from "@chakra-ui/react";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr";
import { useState } from "react";

import useFormatter from "@/hooks/useFormatter";

import { RangeBody } from "./_RangeBody";
import { useRangeDraft } from "./_useRangeDraft";

const getBounds = (state) => {
    const isRentOnly = state.operation.length === 1 && state.operation[0] === "LEASE";
    if (isRentOnly) return { min: 0, max: 150_000, step: 1 };
    return { min: 0, max: 30_000_000, step: 1 };
};

const compactPriceFormatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
});
const formatPriceCompact = (n) => `$${compactPriceFormatter.format(n)}`;

// Wrapper that gives the price draft its own name (used by the mobile drawer
// composition in MoreFiltersPopover — match the sibling useMoreFiltersDraft
// naming convention).
export const usePriceDraft = (state) => useRangeDraft({ state, key: "price", getBounds });

// Price-specific RangeBody wrapper. Exported so the mobile drawer (which
// composes Precio + Tipo + Características + Más filtros into one surface)
// can render the same body the standalone popover uses.
export const PricePopoverBody = ({ draft }) => {
    const { NumberF } = useFormatter();
    const formatLabel = (value, atMax) => {
        const base = NumberF(value, true, "", 0);
        return atMax ? `${base}+` : base;
    };
    const formatInputValue = (value, isEmpty) => (isEmpty ? "" : NumberF(value, true, "", 0));
    return (
        <RangeBody draft={draft} formatLabel={formatLabel} formatInputValue={formatInputValue} />
    );
};

const PricePopover = ({ state, setFilter, clearFilter }) => {
    const [open, setOpen] = useState(false);
    const draft = usePriceDraft(state);

    const isActive = state.price.length > 0;
    const label = (() => {
        if (state.price.length === 0) return "Precio";
        if (state.price.length === 1) return `${formatPriceCompact(state.price[0])}+`;
        return `${formatPriceCompact(state.price[0])} – ${formatPriceCompact(state.price[1])}`;
    })();

    const apply = () => {
        draft.commit(setFilter, clearFilter);
        setOpen(false);
    };
    const clear = () => {
        draft.reset(setFilter, clearFilter);
        setOpen(false);
    };

    return (
        <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
            <Popover.Trigger asChild>
                <Button
                    h="40px"
                    minW="140px"
                    px={4}
                    fontSize="sm"
                    fontWeight={500}
                    bg={isActive ? "colorPalette.solid" : "white"}
                    color={isActive ? "white" : "gray.700"}
                    borderWidth="1px"
                    borderColor={isActive ? "colorPalette.solid" : "gray.200"}
                    borderRadius="md"
                    _hover={{ bg: isActive ? "colorPalette.solid" : "gray.50" }}
                >
                    {label}
                    <Icon ml={2} boxSize={3} opacity={0.7}>
                        <CaretDownIcon weight="bold" />
                    </Icon>
                </Button>
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content colorPalette="accent" w="380px" shadow="lg">
                        <Popover.Body p={4}>
                            <Text fontWeight={600} mb={2}>
                                Precio (MXN){draft.isDirty ? " •" : ""}
                            </Text>
                            <PricePopoverBody draft={draft} />
                            <Flex justify="space-between" gap={2} mt={4}>
                                <Button
                                    onClick={clear}
                                    variant="ghost"
                                    size="sm"
                                    px={5}
                                    borderRadius="md"
                                    color="gray.600"
                                >
                                    Limpiar
                                </Button>
                                <Button
                                    onClick={apply}
                                    size="sm"
                                    px={6}
                                    borderRadius="md"
                                    bg="colorPalette.solid"
                                    color="white"
                                    _hover={{ bg: "colorPalette.emphasized" }}
                                >
                                    Aplicar
                                </Button>
                            </Flex>
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};

export default PricePopover;
